import { API_BASE_URL } from '../constants/appConstants';
import { authenticatedRequest } from './authService';

const INVITATIONS_BASE_URL = `${API_BASE_URL}/invitations`;

export const getUserList = async (page = 1, pageSize = 10, search = '') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/users-list/?${params.toString()}`
    );

    return response;
  } catch (error) {
    let errMsg = "Unknown error";
    if (error?.response?.data) {
      errMsg = error.response.data;
    } else if (error?.message) {
      errMsg = error.message;
    }
    throw errMsg;
  }
};

/**
 * Send an invitation to a user
 * @param {Object} invitationData - { recipient: userId, permission_level: string }
 * @returns {Promise} - The created invitation
 */
export const sendInvitation = async (invitationData) => {
  try {
    const response = await authenticatedRequest(`${INVITATIONS_BASE_URL}/send/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invitationData),
    });

    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Cancel a sent invitation
 * @param {Number} invitationId - ID of the invitation to cancel
 * @returns {Promise} - The updated invitation
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(`${INVITATIONS_BASE_URL}/cancel/${invitationId}/`, {
      method: 'PUT',
    });
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get all invitations sent by the current user
 * @param {Number} page - Page number
 * @param {Number} pageSize - Number of items per page
 * @param {String} statusFilter - Optional filter by invitation status (pending|accepted|rejected|cancel|blocked|paused|all)
 * @returns {Promise} - Paginated list of sent invitations
 */
export const getSentInvitations = async (page = 1, pageSize = 10, statusFilter = 'all') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (statusFilter && statusFilter !== 'all') {
      params.append('status', statusFilter);
    }
    
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/sent/?${params.toString()}`
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get all invitations received by the current user
 * @param {String} statusFilter - Optional filter by invitation status (pending|accepted|rejected|blocked|paused|cancel|all)
 * @param {Number} page - Page number
 * @param {Number} pageSize - Number of items per page
 * @returns {Promise} - Paginated list of received invitations
 */
export const getReceivedInvitations = async (statusFilter = 'all', page = 1, pageSize = 10) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (statusFilter && statusFilter !== 'all') {
      params.append('status', statusFilter);
    }
    
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/received/?${params.toString()}`
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Respond to an invitation (accept or reject)
 * @param {Number} invitationId - ID of the invitation
 * @param {String} action - 'accept' or 'reject'
 * @returns {Promise} - The updated invitation
 */
export const respondToInvitation = async (invitationId, action) => {
  if (action === 'accept') {
    return await acceptInvitation(invitationId);
  } else if (action === 'reject') {
    return await rejectInvitation(invitationId);
  } else {
    throw new Error('Invalid action. Must be "accept" or "reject".');
  }
};

/**
 * Accept an invitation
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - The updated invitation
 */
export const acceptInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/accept/${invitationId}/`,
      {
        method: 'PUT',
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Reject an invitation
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - The updated invitation
 */
export const rejectInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/reject/${invitationId}/`,
      {
        method: 'PUT',
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Block an accepted invitation
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - The updated invitation
 */
export const blockInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/block/${invitationId}/`,
      {
        method: 'PUT',
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Pause an accepted invitation
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - The updated invitation
 */
export const pauseInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/pause/${invitationId}/`,
      {
        method: 'PUT',
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Resume a paused invitation
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - The updated invitation
 */
export const resumeInvitation = async (invitationId) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/resume/${invitationId}/`,
      {
        method: 'PUT',
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Update the permission level of a sent invitation
 * @param {Number} invitationId - ID of the invitation
 * @param {String} permissionLevel - New permission level
 * @returns {Promise} - The updated invitation
 */
export const updateInvitationPermission = async (invitationId, permissionLevel) => {
  try {
    const response = await authenticatedRequest(
      `${INVITATIONS_BASE_URL}/update-permission/${invitationId}/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permission_level: permissionLevel }),
      }
    );
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};

/**
 * Get financial reports summary from a specific inviter
 * @param {Number} invitationId - ID of the invitation
 * @returns {Promise} - Financial reports summary data
 */
export const getInviterReportsSummary = async (invitationId) => {
  try {
    const response = await authenticatedRequest(`${INVITATIONS_BASE_URL}/reports-summary/${invitationId}/`);
    
    return response;
  } catch (error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }
};